import { Component, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { MessageService, SortEvent } from 'primeng/api';
import { TranslateService } from '@ngx-translate/core';
import { debounceTime, distinctUntilChanged, Subject, Subscription } from 'rxjs';
import { EmpresaConfigService } from '../../../services/configuracoes/empresa.service';
import { EmpresaListagemDTO } from '../../../models/configuracoes/empresa-listagem.model';
import { TableColumn } from '../../../models/shared/table-column.model';

/**
 * Area Master — Empresas.
 *
 * Busca o cliente pela EMPRESA (nome, RUC, e-mail, telefone) e leva para a ficha dela.
 * Quem so tem o contato de quem ligou usa a tela de Usuarios (`/master/usuarios`), que
 * atravessa empresas e chega na mesma ficha.
 *
 * Diferente da tela antiga de troca de empresa, aqui NADA muda no usuario logado: o master
 * continua na propria empresa e apenas navega.
 */
@Component({
  selector: 'app-master-empresas',
  templateUrl: './master-empresas.component.html',
  styleUrls: ['../master-listagem.scss'],
  providers: [MessageService],
})
export class MasterEmpresasComponent implements OnInit, OnDestroy {
  // Objetos de traducao
  texts: any = {};
  labels: any = {};
  placeholders: any = {};
  tooltips: any = {};

  empresas: EmpresaListagemDTO[] = [];
  loading = true;

  first = 0;
  rows = 10;
  totalRecords = 0;
  filterName = '';
  lastfield = 'nome';
  lastSort: 'asc' | 'desc' = 'asc';

  readonly STORAGE_KEY_EMPRESAS = 'master-empresas-visible-columns';

  colunasEmpresas: TableColumn[] = [
    { key: 'nome', labelKey: 'master.empresas.labels.nome', visible: true, fixed: true, defaultVisible: true },
    { key: 'ruc', labelKey: 'master.empresas.labels.ruc', visible: true, fixed: false, defaultVisible: true },
    { key: 'email', labelKey: 'master.empresas.labels.email', visible: true, fixed: false, defaultVisible: true },
    { key: 'telefone', labelKey: 'master.empresas.labels.telefone', visible: true, fixed: false, defaultVisible: true },
    { key: 'acoes', labelKey: 'master.empresas.labels.acoes', visible: true, fixed: true, defaultVisible: true },
  ];

  pageSizeOptions = [
    { label: 5, value: 5 },
    { label: 10, value: 10 },
    { label: 20, value: 20 },
    { label: 50, value: 50 },
  ];

  private search$ = new Subject<string>();
  private langChangeSub!: Subscription;

  constructor(
    private messageService: MessageService,
    private empresaConfigService: EmpresaConfigService,
    private translate: TranslateService,
    private router: Router,
  ) {}

  ngOnInit(): void {
    this.loadTranslations();
    this.carregarPreferenciasDeColunas();
    this.setupSearchDebounce();
    this.findEmpresas();

    this.langChangeSub = this.translate.onLangChange.subscribe(() => {
      this.loadTranslations();
    });
  }

  ngOnDestroy(): void {
    this.langChangeSub?.unsubscribe();
  }

  private loadTranslations(): void {
    this.texts = {
      titulo: this.translate.instant('master.empresas.texts.titulo'),
      subtitulo: this.translate.instant('master.empresas.texts.subtitulo'),
      tabela_de_empresas: this.translate.instant('master.empresas.texts.tabela_de_empresas'),
      nenhuma_empresa: this.translate.instant('master.empresas.texts.nenhuma_empresa'),
      dica_busca: this.translate.instant('master.empresas.texts.dica_busca'),
      carregando: this.translate.instant('master.empresas.texts.carregando'),
    };

    this.labels = {
      nome: this.translate.instant('master.empresas.labels.nome'),
      ruc: this.translate.instant('master.empresas.labels.ruc'),
      email: this.translate.instant('master.empresas.labels.email'),
      telefone: this.translate.instant('master.empresas.labels.telefone'),
      acoes: this.translate.instant('master.empresas.labels.acoes'),
      itens_por_pagina: this.translate.instant('master.empresas.labels.itens_por_pagina'),
    };

    this.placeholders = {
      pesquisar: this.translate.instant('master.empresas.placeholders.pesquisar'),
    };

    this.tooltips = {
      abrir: this.translate.instant('master.empresas.tooltips.abrir'),
    };
  }

  // =========================================================================
  // Colunas (mesmo contrato do ColumnSelectorComponent das demais listagens)
  // =========================================================================

  isColunaEmpresaVisivel(key: string): boolean {
    return this.colunasEmpresas.find((c) => c.key === key)?.visible ?? true;
  }

  get colunasEmpresasVisiveis(): number {
    return this.colunasEmpresas.filter((c) => c.visible).length;
  }

  onColunasEmpresasChange(colunas: TableColumn[]): void {
    this.colunasEmpresas = [...colunas];
  }

  /** Restaura o que o usuario escolheu da ultima vez (mesmo formato das outras listagens). */
  private carregarPreferenciasDeColunas(): void {
    try {
      const salvo = localStorage.getItem(this.STORAGE_KEY_EMPRESAS);
      if (!salvo) return;
      const visiveis = JSON.parse(salvo) as string[];
      this.colunasEmpresas.forEach((col) => {
        if (!col.fixed) {
          col.visible = visiveis.includes(col.key);
        }
      });
    } catch (e) {
      console.warn('Erro ao carregar preferências de colunas:', e);
    }
  }

  // =========================================================================
  // Busca
  // =========================================================================

  private setupSearchDebounce(): void {
    this.search$
      .pipe(debounceTime(600), distinctUntilChanged())
      .subscribe(() => {
        this.first = 0;
        this.findEmpresas(0);
      });
  }

  onSearchChange(value: string): void {
    this.filterName = value || '';
    this.search$.next(this.filterName);
  }

  // =========================================================================
  // Empresas
  // =========================================================================

  findEmpresas(page: number = 0, size: number = this.rows): void {
    this.loading = true;
    const sort = `${this.lastfield},${this.lastSort}`;
    this.empresaConfigService.listarEmpresas(this.filterName, page, size, sort).subscribe({
      next: (response) => {
        this.empresas = response.content || [];
        this.totalRecords = response.totalElements || 0;
        this.first = page * size;
        this.loading = false;
      },
      error: () => {
        this.empresas = [];
        this.totalRecords = 0;
        this.loading = false;
        this.messageService.add({
          severity: 'error',
          summary: this.translate.instant('common.erro'),
          detail: this.translate.instant('master.empresas.mensagens.erro_carregar'),
        });
      },
    });
  }

  onPageChange(event: any): void {
    this.rows = event.rows;
    this.findEmpresas(event.page, event.rows);
  }

  onRowsPerPageChange(): void {
    this.first = 0;
    this.findEmpresas(0, this.rows);
  }

  /** Ordenacao server-side, no formato do Spring — igual as demais listagens paginadas. */
  onSortEmpresas(evento: SortEvent): void {
    const campo = evento.field || this.lastfield;
    const ordem: 'asc' | 'desc' = evento.order === -1 ? 'desc' : 'asc';
    if (campo === this.lastfield && ordem === this.lastSort) return;
    this.lastfield = campo;
    this.lastSort = ordem;
    this.first = 0;
    this.findEmpresas(0);
  }

  /** Abre a ficha da empresa. Nenhuma troca de empresa acontece aqui. */
  abrirEmpresa(empresa: EmpresaListagemDTO): void {
    if (empresa?.id == null) return;
    this.router.navigate(['/master/empresas', empresa.id]);
  }
}
